import { UserConsentRecords } from '@cf-shared/user-consent-interface';
import { ConsentRefresh } from '@cf-shared/consent-refresh';

// Which consent dialog, if any, the app-level consent dialog shows.
export enum ConsentDialogMode {
  None = 0,
  Cookies = 1, // generic cookie dialog
  User = 2 // user consent (Terms of Service) dialog
}

// The one answer that is not a mode: the inputs cannot decide yet, so whatever
// the dialog currently shows stays. This happens for a signed-in, online user
// outside a webview whose CouchDB login has not arrived — the account consent
// records only come with that login. It is named here rather than left as a
// branch with no `else`, because that hole is what left the cookie dialog stuck
// open after Save Settings / Accept All (#4219).
export enum ConsentModeUndecided {
  KeepCurrent = 'keep-current'
}

export type ConsentModeDecision = ConsentDialogMode | ConsentModeUndecided;

export interface ConsentModeInputs {
  signedIn: boolean;
  // the only inputs read for a signed-out user
  isNative: boolean;
  functionalityCookieGranted: boolean;
  // the only inputs read for a signed-in user
  isWebView: boolean;
  offline: boolean;
  // the CouchDB login result, or null while it has not arrived. Only its presence
  // is read now: what the account's consent records mean is decided by
  // `decideConsentRefresh`, whose answer arrives as `consentRefresh`.
  couchLogin: { consents?: UserConsentRecords } | null;
  // what this account owes the consent screen (#5587): nothing, a gentle ask
  // while the text is newly changed, or the blocking screen
  consentRefresh: ConsentRefresh;
  // true while the person opened the screen themselves from the home dashboard's
  // "we updated the policy" card. A gentle ask then shows the screen too — with a
  // Later button, and without blocking the app
  openedForReview: boolean;
}

/**
 * Decide which consent dialog should show. Pure: it reads only its inputs.
 *
 * - Signed out: the cookie banner, unless this is a native app (no browser
 *   cookies to gate) or the functionality cookie is already granted.
 * - Signed in: never the Terms of Service dialog in a webview or while offline.
 *   Otherwise `consentRefresh` decides — and with no CouchDB login yet there are
 *   no records for it to have read, so the answer is `KeepCurrent`.
 *
 * The three refresh answers (#5587): `None` shows nothing; `Require` shows the
 * screen, which then blocks the app, for an account that never accepted or whose
 * month of grace is up; `Ask` shows it only when the person opened it themselves
 * from the home card, which is what asks meanwhile.
 */
export function decideConsentMode(inputs: ConsentModeInputs): ConsentModeDecision {
  // if the user is not signed in
  if (!inputs.signedIn) {
    if (inputs.isNative || inputs.functionalityCookieGranted) {
      return ConsentDialogMode.None;
    }
    return ConsentDialogMode.Cookies;
  }

  // if in a webview or offline, the full ToS dialog is never shown
  if (inputs.isWebView || inputs.offline) {
    return ConsentDialogMode.None;
  }

  // if the CouchDB login has not delivered the account records yet, keep what shows
  if (!inputs.couchLogin) {
    return ConsentModeUndecided.KeepCurrent;
  }

  // the account's answer is current: nothing to show
  if (inputs.consentRefresh === ConsentRefresh.None) {
    return ConsentDialogMode.None;
  }
  // the blocking screen, or a gentle ask the person chose to open
  if (inputs.consentRefresh === ConsentRefresh.Require || inputs.openedForReview) {
    return ConsentDialogMode.User;
  }
  // a gentle ask nobody opened: the home dashboard's card carries it instead
  return ConsentDialogMode.None;
}

/**
 * Whether the cookie dialog says WHY Advertising is off (#5682): only while this
 * browser has sent the Global Privacy Control signal and Advertising is off.
 *
 * `gpcSince` is the stamp the signal left (#5663); its presence means the signal
 * was seen at some point. `advertisingOn` is the toggle as it stands right now,
 * so the line goes away the moment the person switches Advertising on themselves
 * — it explains the off state, and has nothing to say once there is none.
 *
 * It is a statement, never a prompt. California forbids asking a consumer to
 * authorise sharing again within 12 months of an opt-out, so nothing here may
 * invite the person to turn Advertising back on.
 */
export function shouldShowGpcNotice(gpcSince: string | undefined, advertisingOn: boolean): boolean {
  return !!gpcSince && !advertisingOn;
}


/** Why the Terms screen will not open when the person asks to review it (#5812). */
export enum TermsReviewRefusal {
  // it opens
  None = 'none',
  // no connection, or the CouchDB login that carries the records has not arrived:
  // the screen could not save an answer anyway
  Offline = 'offline',
  // the account already holds the current answer, so there is nothing to ask. The
  // card that offered the button is out of date.
  AlreadyAnswered = 'already-answered',
  // signed out or in a webview, where the Terms screen is never shown. The card is
  // not drawn in either, so reaching this is unexpected.
  Unavailable = 'unavailable',
}

/**
 * Why the Terms screen refuses to open for a person who asked to review it, from
 * the home dashboard's "we updated the policy" card (#5812).
 *
 * `decideConsentMode` answers only "show nothing" in each of these cases, and the
 * Review button then did nothing at all. The reachable case is a connection that
 * dropped after the card was drawn. The dialog uses this answer to tell the person
 * why, or to make the out-of-date card go away. Pure, like `decideConsentMode`,
 * whose checks it names one by one; pass the inputs with `openedForReview` set.
 */
export function whyTermsReviewCannotOpen(inputs: ConsentModeInputs): TermsReviewRefusal {
  if (decideConsentMode(inputs) === ConsentDialogMode.User) return TermsReviewRefusal.None;
  if (!inputs.signedIn || inputs.isWebView) return TermsReviewRefusal.Unavailable;
  // offline wins over "already answered": an offline copy of the records is not
  // proof the answer is current, and the person can only act once connected
  if (inputs.offline || !inputs.couchLogin) return TermsReviewRefusal.Offline;
  return TermsReviewRefusal.AlreadyAnswered;
}
